import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

const getPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) { reject(new Error("Location isn't available on this device")); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    (e) => reject(new Error(e.code === 1 ? "Allow location access to clock in or out" : "Couldn't get your location. Try again outside.")),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
  );
});

export function ClockCard({ onChange }) {
  const [st, setSt] = useState(null);
  const [site, setSite] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [other, setOther] = useState({ name: "", address: "" });
  const load = useCallback(() => api.get("/clock/status").then((r) => { setSt(r.data); if (r.data.sites?.length === 1) setSite(r.data.sites[0].id); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!st || st.mode !== "geofence") return null;

  const act = async (kind) => {
    setBusy(true); setErr("");
    try {
      const pos = await getPosition();
      const { data: ev } = await api.post(`/clock/${kind}`, kind === "in" ? { ...pos, site_id: site, other_site_name: other.name, other_site_address: other.address } : pos);
      const d = kind === "in" ? ev?.in_distance_m : ev?.out_distance_m;
      const far = d == null ? "" : d >= 1000 ? ` (${(d / 1000).toFixed(1)} km from the site)` : ` (${d} m from the site)`;
      if (kind === "out" && ev?.out_outside) toast.warning(`Clocked out${far}. You were outside the site area, so your Admin has been notified.`);
      else toast.success(kind === "in" ? `Clocked in${far}` : `Clocked out${far}. Added to your timesheet`);
      load(); onChange?.();
    } catch (e) {
      const msg = e.response ? formatApiError(e.response.data?.detail) : e.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const open = st.open;
  return (
    <div data-testid="clock-card" className="bg-slate-900 text-white rounded-lg p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Geo-fence clock</div>
        {open && <span data-testid="clock-status-in" className="text-[11px] font-bold bg-emerald-500 text-black px-2 py-0.5 rounded-full">On the clock</span>}
      </div>
      {open ? (
        <div className="text-sm mb-3">Clocked in at <span className="num font-bold">{open.in_local}</span> · {open.site_name}</div>
      ) : (
        <>
        {st.sites.length === 0 && <div className="text-xs text-zinc-300 mb-2" data-testid="clock-no-sites">No geo-fenced sites are assigned to you. Pick "Other site" if you're working somewhere new.</div>}
        <Select value={site} onValueChange={setSite}>
          <SelectTrigger data-testid="clock-site" className="mb-3 bg-white text-slate-900"><SelectValue placeholder="Choose your site" /></SelectTrigger>
          <SelectContent>{st.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}<SelectItem value="__other__">Other site (not listed)…</SelectItem></SelectContent>
        </Select>
        </>
      )}
      {!open && site === "__other__" && <div className="grid gap-2 mb-3">
        <Input data-testid="clock-other-name" className="bg-white text-slate-900" placeholder="Site name" value={other.name} onChange={(e) => setOther({ ...other, name: e.target.value })} />
        <Input data-testid="clock-other-address" className="bg-white text-slate-900" placeholder="Address (optional)" value={other.address} onChange={(e) => setOther({ ...other, address: e.target.value })} />
        <p className="text-[11px] text-amber-300">No geo-fence check. Your admin will review this site.</p>
      </div>}
      {open ? (
        <Button data-testid="clock-out-btn" disabled={busy} onClick={() => act("out")} className="w-full h-12 bg-rose-500 hover:bg-rose-600 text-white font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1.5" />Clock out</>}</Button>
      ) : (
        <Button data-testid="clock-in-btn" disabled={busy || !site} onClick={() => act("in")} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1.5" />Clock in</>}</Button>
      )}
      {err && <div data-testid="clock-error" className="text-xs text-rose-300 mt-2">{err}</div>}
      {st.today?.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-2 space-y-1">
          {st.today.map((e) => <div key={e.id} className="text-xs text-zinc-300 flex justify-between"><span>{e.site_name}</span><span className="num">{e.in_local}–{e.out_local}</span></div>)}
        </div>
      )}
    </div>
  );
}
